import i18next from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTable, { TableColumn } from 'react-data-table-component';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';
import { tableHelper } from 'common/helpers/table/table-helper';
import {
    MasterLayout,
    MainLayout,
    ToolbarLayout,
    ToolbarLayoutLeft,
    ToolbarLayoutRight,
} from 'common/imports/content-layout';
import { DeleteModal } from 'common/partials/DeleteModal';
import { itLabTableStyle } from 'common/helpers/table/table-style';
import { RolesDictionary } from 'common/helpers/dictionaries/roles-dictionary';
import ActionButton from 'common/partials/ActionButton';
import en from '../i18n/en-us.json';
import pt from '../i18n/pt-br.json';
import es from '../i18n/es-es.json';
import { APIClientsModel as Model } from '../model/api-clients.model';
import { APIClientsService as Service } from '../services/api-clients.service';
import { CreateEditAPIClient } from './CreateEditAPIClient';

export function APIClients() {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);
    i18next.addResourceBundle('es', 'translation', es);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tableData, setTableData] = useState<Model[]>([]);
    const [filteredTableData, setFilteredTableData] = useState<Model[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState<Model[]>([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [editId, setEditId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(100);
    const [sorting, setSorting] = useState<{ orderBy?: string; order?: 'asc' | 'desc' }>({
        orderBy: 'Id',
        order: 'desc'
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteId, setDeleteId] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);


    const handleShowDeleteModal = (id: number) => {
        setShowDeleteModal(true);
        setDeleteId(id);
    };

    const handleCloseDeleteModal = () => setShowDeleteModal(false);
    const [openCreateEditModal, setOpenCreateEditModal] = useState(false);
    const handleCreateEditModalOpen = (id: any) => {
        setEditId(id)
        setOpenCreateEditModal(true)
    };
    const handleCreateEditModalClose = () => {
        setOpenCreateEditModal(false);
    };

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const response = await Service.getDataList({
                pageNumber: currentPage,
                pageSize: rowsPerPage,
                orderBy: sorting.orderBy,
                order: sorting.order
            });
            if (response && !response.hasErrors) {
                setTableData(response.data.items);
                setFilteredTableData(response.data.items);
                setTotalRows(response.data.totalCount);
            }
        } catch (err) {
            toast.error(`${t('crud.read.errorMessage')}`);
        } finally {
            setIsLoading(false);
        }
    };

    function handleFilterByName(textField: string) {
        setIsLoading(true);

        if (textField === '') {
            setIsLoading(false);
            setFilteredTableData(tableData);
            return;
        }

        const normalizedFilter = textField.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        setFilteredTableData(
            tableData.filter((item) =>
                item.clientId
                    ?.normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase()
                    .includes(normalizedFilter.toLowerCase()),
            ),
        );

        setIsLoading(false);
    }

    const handleSearch = (query: string) => {
        setIsLoading(true);
        try {
            setSearchQuery(query);
            handleFilterByName(query);
            setCurrentPage(1);
        } finally {
            setIsLoading(false);
        }
    };

    const columns: TableColumn<Model>[] = [
        {
            name: `${t('clientsApi.tableHeaders.id')}`,
            selector: (row) => row.id || '',
            sortable: true,
            sortField: 'Id',
            width: '60px',
        },
        {
            name: `${t('clientsApi.tableHeaders.clientId')}`,
            selector: (row) => row.clientId || '',
            sortable: true,
            sortField: 'ClientId'
        },
        {
            name: `${t('clientsApi.tableHeaders.description')}`,
            selector: (row) => row.description || '',
            sortable: true,
            sortField: 'Description'
        },
        {
            name: `${t('clientsApi.tableHeaders.type')}`,
            cell: (row) =>
                row.type === 0 ? (
                    <span className="badge badge-light-warning">{t('clientsApi.form.confidential')}</span>
                ) : (
                    <span className="badge badge-light-primary">{t('clientsApi.form.public')}</span>
                ),
        },
        {
            name: `${t('clientsApi.tableHeaders.updateTokenTime')}`,
            selector: (row) => row.updateTokenTime || '-',
            width: '200px'
        },
        {
            name: `${t('clientsApi.tableHeaders.origin')}`,
            selector: (row) => row.origin || '-',
        },
        {
            name: `${t('clientsApi.tableHeaders.status')}`,
            cell: (row) =>
                row.isActive ? (
                    <span className="badge badge-light-primary">{t('generalMessages.active')}</span>
                ) : (
                    <span className="badge badge-light-danger">{t('generalMessages.inactive')}</span>
                ),
            width: '150px',
        },
        {
            name: `${t('clientsApi.tableHeaders.actions')}`,
            cell: (row) =>
                row.id && (
                    <>
                        <div className="d-grid d-flex gap-2">
                            <ActionButton type='edit' onClick={() => handleCreateEditModalOpen(row.id!)} roles={[RolesDictionary.Security.APIClients]} />
                            <ActionButton type='deactivate' onClick={() => handleShowDeleteModal(row.id!)} roles={[RolesDictionary.Security.APIClients]} disabled={!row.isActive} />
                        </div>
                        <Tooltip title={t('clientsApi.tooltip.editUser')} arrow>
                            <button
                                type="button"
                                className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm ms-2"
                                onClick={() => navigate(`/security/usermanagement/${row.userId}`)}
                            >
                                <span className="svg-icon svg-icon-3">
                                    <i className="fa-solid fa-user-gear"></i>
                                </span>
                            </button>
                        </Tooltip>
                    </>
                ),
            width: '130px',
        },
    ];

    const handleRowSelected = useCallback((state: any) => {
        setSelectedRows(state.selectedRows);
    }, []);

    const deleteDataById = async () => {
        try {
            setIsLoading(true);
            const result = await Service.deleteDataById(deleteId);
            if (result && !result.hasErrors) {
                toast.success(`${t('crud.deactivate.successMessage')}`);
            } else {
                toast.error(`${t('crud.deactivate.errorMessage')}`);
            }
        } catch (err) {
            toast.error(`${t('crud.read.errorMessage')}`);
        } finally {
            setToggleCleared(!toggleCleared);
            handleCloseDeleteModal();
            fetchData();
            setIsLoading(false);
        }
    };

    const contextActions = useMemo(() => {
        const deleteSelectedRows = () => {
            setShowDeleteModal(true);
            selectedRows.forEach((row) => {
                setDeleteId(row.id!);
            });
        }

        return (
            <button type="button" className="btn btn-sm btn-danger" onClick={deleteSelectedRows}>
                {t('crud.deactivate.buttonSave')}
            </button>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRows, toggleCleared]);

    useEffect(() => {
        const fetchDataWithLoading = async () => {
            try {
                await fetchData();
            } catch (error) {
                setIsLoading(false);
            }
        };

        fetchDataWithLoading();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, rowsPerPage, sorting]);

    const handleListSort = (selectedColumn: TableColumn<Model>, sortDirection: 'asc' | 'desc') => {
        setIsLoading(true);
        setSorting({
            orderBy: selectedColumn.sortField || 'Id',
            order: sortDirection
        });

        setTableData([]);
        setFilteredTableData([]);
    };

    return (
        <MasterLayout>
            <ToolbarLayout>
                <ToolbarLayoutLeft />
                <ToolbarLayoutRight>
                    <div className="d-flex justify-content-end">
                        <button type="button" onClick={() => handleCreateEditModalOpen(0)} className="btn btn-sm btn-primary">
                            {t('clientsApi.toolbar.addClient')}
                        </button>
                    </div>
                </ToolbarLayoutRight>
            </ToolbarLayout>
            <MainLayout>
                <div className="card card-flush">
                    <div className="card-body">
                        <DataTable
                            title={tableHelper.TableHeader((e: any) => handleSearch(e.target.value))}
                            persistTableHead
                            columns={columns}
                            data={filteredTableData}
                            noDataComponent={tableHelper.NoDataComponent()}
                            selectableRows
                            selectableRowsHighlight
                            dense
                            highlightOnHover
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationPerPage={rowsPerPage}
                            paginationRowsPerPageOptions={tableHelper.paginationRowsPerPageOptions}
                            onChangePage={(page) => setCurrentPage(page)}
                            onChangeRowsPerPage={(newPerPage, page) => {
                                setRowsPerPage(newPerPage);
                                setCurrentPage(page);
                            }}
                            progressPending={isLoading}
                            progressComponent={tableHelper.Loader()}
                            contextActions={contextActions}
                            contextMessage={tableHelper.ContextMessage()}
                            onSelectedRowsChange={handleRowSelected}
                            clearSelectedRows={toggleCleared}
                            selectableRowsVisibleOnly
                            customStyles={itLabTableStyle}
                            onSort={handleListSort}
                            sortServer
                        />
                    </div>
                </div>

                <CreateEditAPIClient editId={editId} open={openCreateEditModal} handleClose={handleCreateEditModalClose} fetchData={fetchData} />
                <DeleteModal
                    type='deactivate'
                    handleDelete={deleteDataById}
                    onClose={handleCloseDeleteModal}
                    open={showDeleteModal}
                />
            </MainLayout>
        </MasterLayout>
    );
}
